/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { Button } from "@evertrue/et-components";
import OverlayInstructions from "./overlay-instructions";
import ResolutionComponent from "./thankview-recorder-resolutions";
import { useRecorder } from "./hooks/recorder-context";
import VideoPlayer from "./thankview-recorder-base";
import CountdownTimer from "./countdown-timer";
import DeviceSelector from "./device-selector";
import ThankviewSettings from "./thankview-settings";
import CopyVideo from "./copy-video";
import { updateVideo } from "../../entities/helpers/thankview-helper";

const VideoControl = ({
  isRecording,
  webcamEnabled,
  onEnableWebcam,
  onStartRecording,
  onStopRecording,
  onRerecord,
}) => (
  <>
    {isRecording && (
      <Button type="action" aria-label="Start Over" onClick={onRerecord}>
        Start Over
      </Button>
    )}
    {!webcamEnabled ? (
      <Button type="action" aria-label="Enable Webcam" onClick={onEnableWebcam}>
        Enable Webcam
      </Button>
    ) : isRecording ? (
      <Button type="action" aria-label="Stop Recording" onClick={onStopRecording} className="stop-recording">
        Stop Recording
      </Button>
    ) : (
      <Button type="action" aria-label="Start Recording" onClick={onStartRecording} className="start-recording">
        Start Recording
      </Button>
    )}{" "}
  </>
);

const ScriptToggle = ({ showScript, onToggle }) => (
  <Button className="et--button-secondary" onClick={onToggle}>
    {showScript ? "Hide Script" : "Show Script"}
  </Button>
);

const ThankviewRecorder = () => {
  const {
    state,
    videoRef,
    reviewVideoRef,
    enableWebcam,
    startRecording,
    stopRecording,
    rerecord,
    startCountdown,
    resetTimestamp,
    selectedAudioDevice,
    selectedVideoDevice,
    audioDevices,
    videoDevices,
    setSelectedAudioDevice,
    setSelectedVideoDevice,
    color,
    trackColor,
    handleRetry,
    selectedVideo,
    clientId,
  } = useRecorder();
  const { webcamStream, webcamEnabled, isRecording, videoUrl } = state;
  const [videoName, setVideoName] = useState("");
  const [showScript, setShowScript] = useState(false);
  const [accordionOpen, setAccordionOpen] = useState(false);
  const [script, setScript] = useState("");
  const accordionContentRef = useRef(null);
  useEffect(() => {
    const controls = document.querySelector(".controls");
    if (controls) {
      controls.style.backgroundColor = color;
    }
    const slider = document.querySelector(".time-track");
    if (slider) {
      slider.style.setProperty("--track-color", trackColor);
    }
  }, [color, trackColor]);

  useEffect(() => {
    if (selectedVideo && selectedVideo.data && selectedVideo.data.personalVideo) {
      setVideoName(selectedVideo.data.personalVideo.name || "");
    }
  }, [selectedVideo]);

  const handleInputChange = (e) => {
    const newName = e.target.value;
    setVideoName(newName);
  };

  const handleUpdateVideo = () => {
    const formattedChanges = {
      personalVideo: { name: videoName },
    };

    updateVideo(
      selectedVideo.data.personalVideo.id,
      formattedChanges,
      clientId.data.client_id,
      clientId.data.client_secret
    )
      .then(() => {
        console.log("Video name updated successfully");
      })
      .catch((error) => {
        console.error("Failed to update video name:", error);
      });
  };
  const formatTime = (seconds) => {
    var minutes = Math.floor(seconds / 60);
    var remainingSeconds = seconds % 60;
    return minutes.toString().padStart(2, "0") + ":" + remainingSeconds.toString().padStart(2, "0");
  };

  const toggleScript = () => setShowScript(!showScript);
  const handleScriptChange = (event) => setScript(event.target.value);

  const handleEnableWebcam = async () => {
    try {
      const constraints = {
        audio: { deviceId: selectedAudioDevice ? { exact: selectedAudioDevice } : undefined },
        video: { deviceId: selectedVideoDevice ? { exact: selectedVideoDevice } : undefined },
      };
      await enableWebcam(constraints);
    } catch (error) {
      console.error("Error enabling webcam:", error);
    }
  };
  const handleStartRecording = () => {
    resetTimestamp();
    startCountdown();
    startRecording();
  };

  return (
    <div id="thankview-recorder-container" className="thankview-recorder-container">
      {showScript && (
        <OverlayInstructions overlayInstructions={script} modifyScript={handleScriptChange} toggleOff={toggleScript} />
      )}
      {webcamStream && isRecording && !state.isCountingDown && (
        <>
          <div className="recorder-top-buttons" style={{ zIndex: 1002 }}>
            <div className="top-left-buttons">
              <div className="timestamp">{formatTime(state.timestamp)}/5:00</div>
            </div>
            <div className="top-right-buttons">
              <ResolutionComponent />
              <DeviceSelector
                devices={audioDevices}
                selectedDevice={selectedAudioDevice}
                onDeviceChange={setSelectedAudioDevice}
                label="Microphone"
              />
              <DeviceSelector
                devices={videoDevices}
                selectedDevice={selectedVideoDevice}
                onDeviceChange={setSelectedVideoDevice}
                label="Camera"
              />
            </div>
          </div>
        </>
      )}
      {!videoUrl && !state.isCountingDown && !state.isReviewing && (
        <div className="tv-recorder-buttons" style={{ zIndex: 1001 }}>
          <>
            {/* <ScriptToggle showScript={showScript} onToggle={toggleScript} /> */}
            <VideoControl
              isRecording={isRecording}
              webcamEnabled={webcamEnabled}
              onEnableWebcam={handleEnableWebcam}
              onStartRecording={handleStartRecording}
              onStopRecording={stopRecording}
              onRerecord={rerecord}
            />
          </>
        </div>
      )}
      {videoRef && !state.isReviewing && !state.isProcessing && (
        <div className="video-container">
          <VideoPlayer
            id="recorder"
            ref={videoRef}
            controls={false}
            stream={webcamStream}
            autoPlay={true}
            isRecording={true}
          />
          {state.isCountingDown && <CountdownTimer onCountdownEnd={handleStartRecording} />}
        </div>
      )}
      {state.isProcessing && (
        <>
          <div style={{ textAlign: "center", height: "730px", marginBottom: "5px" }}>
            <div style={{ justifyContent: "center", marginTop: "100px" }}>
              <div style={{ marginBottom: "10px" }}>
                <i className="fa fa-spinner-tv fa-spin"></i>
              </div>
              <div>
                <p className="processing-video-text">Processing Video...</p>
                <Button onClick={handleRetry}>Cancel & Retry</Button>
              </div>
            </div>
          </div>
        </>
      )}
      {state.isReviewing && !state.isProcessing && (
        <div className="flex flex-col h-full">
          <div className="flex justify-between flex-1">
            <div className="left-options-container">
              <div className="open-left">
                <div className="accordion-item">
                  <div className="cursor-pointer" onClick={() => setAccordionOpen(!accordionOpen)}>
                    <span className={`accordion-icon ${accordionOpen ? "rotate-90" : "rotate-270"}`}>
                      <i className="fa fa-cog"></i>
                      {accordionOpen ? <i className="fa fa-chevron-right"></i> : <i className="fa fa-chevron-left"></i>}
                    </span>
                  </div>
                  <div
                    ref={accordionContentRef}
                    style={{
                      overflowY: accordionOpen ? "scroll" : "hidden",
                      maxHeight: accordionOpen ? "500px" : "0",
                      opacity: accordionOpen ? 1 : 0,
                      maxWidth: accordionOpen ? "480px" : "50px",
                      transition: "max-height 0.3s ease-out, opacity 0.3s ease-out",
                    }}
                  >
                    <ThankviewSettings selectedVideo={selectedVideo} />
                  </div>
                </div>
              </div>
            </div>
            <div className="w-2/3">
              <input
                type="text"
                className="video-title-input"
                onChange={handleInputChange}
                onBlur={handleUpdateVideo}
                name="Video Title"
                placeholder={selectedVideo.name}
                value={videoName}
              />
              <VideoPlayer
                id="player-review"
                src={videoUrl ? videoUrl : selectedVideo.data.libraryVideo.video_path}
                ref={reviewVideoRef}
                controls={true}
                autoPlay={false}
                isRecording={false}
              />
            </div>
          </div>
          <div className="flex justify-end margin-top-before-buttons">
            <CopyVideo
              videoThumbnail={
                selectedVideo.data.libraryVideo.animated_thumb_path
                  ? selectedVideo.data.libraryVideo.animated_thumb_path
                  : selectedVideo.data.libraryVideo.video_thumb
              }
            />
            <Button type="action" onClick={rerecord}>
              Go Back
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

VideoControl.propTypes = {
  isRecording: PropTypes.bool.isRequired,
  webcamEnabled: PropTypes.bool.isRequired,
  onEnableWebcam: PropTypes.func.isRequired,
  onStartRecording: PropTypes.func.isRequired,
  onStopRecording: PropTypes.func.isRequired,
  onRerecord: PropTypes.func.isRequired,
};

ScriptToggle.propTypes = {
  showScript: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
};

export default ThankviewRecorder;
